import type {
    PlannableTriggerKind,
    VirtualAppointmentTriggerKind,
    VirtualAppointmentWeeksFrom
} from '../../services/virtualAppointments';
import { formatDateOnly } from '../../utils/dates.ts';
import { plannedNogosBaseline, type SavedNogoBaseline } from './virtualAppointmentNogoBaseline.ts';
import { currentTreatmentWeek, utcDayOf, weeksDueDate } from './virtualAppointmentWeeks.ts';

// The always-visible line under a trigger: where its count starts. Rules stay in the tooltip.
const UNKNOWN_START_BASELINE = 'Counted from when the patient enters the Treatment phase.';
const SNOOZE_NOGOS_BASELINE = 'Restarts at 0: counts no-gos from scans taken after this snooze.';

export interface AlignerProgress {
    current: number | null;
    total: number | null;
}

export const UNKNOWN_ALIGNER_PROGRESS: AlignerProgress = { current: null, total: null };

// "Current aligner: 14 of 40."; null while the current aligner is unknown.
export function alignerBaseline({ current, total }: AlignerProgress): string | null {
    if (current === null) return null;
    return total === null
        ? `Current aligner: ${current}.`
        : `Current aligner: ${current} of ${total}.`;
}

export interface WeeksBaselineInput {
    weeks: number | null;
    weeksFrom: VirtualAppointmentWeeksFrom;
    /** The saved "from now" anchor (ISO) the trigger keeps; null for a new or re-stamped one. */
    keptAnchor: string | null;
    treatmentStartDate: string | null;
    todayIso: string;
}

// "Week 18 ≈ Oct 20, 2026." is added once a week still ahead is entered.
function withDueDay(base: string, weeks: number | null, countStart: string, current: number) {
    const due = weeks !== null && weeks > current ? weeksDueDate(countStart, weeks) : null;
    return due === null ? base : `${base} Week ${weeks} ≈ ${formatDateOnly(due)}.`;
}

function treatmentStartBaseline(
    weeks: number | null,
    treatmentStartDate: string | null,
    todayIso: string
): string {
    const current = currentTreatmentWeek(treatmentStartDate, todayIso);
    if (treatmentStartDate === null || current === null) return UNKNOWN_START_BASELINE;
    const start = formatDateOnly(treatmentStartDate);
    const base = `Counted from the Treatment-phase start, ${start} (now week ${current}).`;
    return withDueDay(base, weeks, treatmentStartDate, current);
}

function anchorBaseline(weeks: number | null, anchorDay: string, todayIso: string): string {
    const current = currentTreatmentWeek(anchorDay, todayIso) ?? 0;
    const base = `Counted from ${formatDateOnly(anchorDay)} (now week ${current}).`;
    return withDueDay(base, weeks, anchorDay, current);
}

export function plannedWeeksBaseline(input: WeeksBaselineInput): string {
    const { weeks, todayIso } = input;
    if (input.weeksFrom === 'treatment_start') {
        return treatmentStartBaseline(weeks, input.treatmentStartDate, todayIso);
    }
    const anchorDay = utcDayOf(input.keptAnchor);
    if (anchorDay !== null) return anchorBaseline(weeks, anchorDay, todayIso);
    return withDueDay(`Counted from today, ${formatDateOnly(todayIso)}.`, weeks, todayIso, 0);
}

export interface PlannedTriggerBaselineInput extends WeeksBaselineInput {
    triggerKind: VirtualAppointmentTriggerKind;
    aligners: AlignerProgress;
    /** The saved VA's running count and window when it already counted no-gos; null for a new one. */
    savedNogo: SavedNogoBaseline | null;
}

export function plannedTriggerBaseline(input: PlannedTriggerBaselineInput): string | null {
    switch (input.triggerKind) {
        case 'aligner':
            return alignerBaseline(input.aligners);
        case 'weeks':
            return plannedWeeksBaseline(input);
        case 'nogos':
            return plannedNogosBaseline(input.savedNogo);
        case 'now':
            return null;
    }
}

export interface SnoozeTriggerBaselineInput extends Omit<WeeksBaselineInput, 'keptAnchor'> {
    triggerKind: PlannableTriggerKind;
    aligners: AlignerProgress;
}

// A snooze always writes a new trigger, so a "from now" week counts from today.
export function snoozeTriggerBaseline(input: SnoozeTriggerBaselineInput): string | null {
    switch (input.triggerKind) {
        case 'aligner':
            return alignerBaseline(input.aligners);
        case 'weeks':
            return plannedWeeksBaseline({ ...input, keptAnchor: null });
        case 'nogos':
            return SNOOZE_NOGOS_BASELINE;
    }
}
